// Copyright 2020 Drivetrain Hub LLC
// Vuex module for managing state of drivetrain model document.
// `namespaced` option applies to getters, actions, and mutations; state is always namespaced.

import {assignProps, copyDeep, isDefined} from "../../assets/js/utils";
import {getObjectById, removeObjectById, updateObject, replaceObjectById, updateObjectById} from "../helpers";
import {apolloFieldsString, apolloMutate, apolloQuery, apolloStore} from "../../config/apollo";
import defaults from "../../config/defaults";
import requests from "../../requests";
import handlers from "../../handlers";
import { EventBus } from "../../event_bus"
import {Notification, Message, MessageBox} from 'element-ui';
import gql from "graphql-tag";

// region PART FRAGMENTS
// PLACEHOLDER {maintenance} Properly organize usage of Apollo fragments; currently they are defined ad-hoc.
//  Each fragment is meant to be fetch data for reuse throughout app.
//  This means each fragment name must be unique.
//  https://www.apollographql.com/docs/ios/fragments/
//  [ ] Add fragments as constants to $requests.

const inlineFragmentViewer = `
  ...on Part {
      Id
      viewer {
        ${requests.model.PartViewer}
      }
    }`;

const fragmentViewer = gql`
  fragment partViewer on Part {
    ${inlineFragmentViewer}
  }`;

const fragmentPart = gql`
  fragment part on Part {
    ${requests.model.fragmentPart}
  }`;

const fragmentEntirePart = gql`
  fragment entirePart on Part {
    ${requests.model.partQueryStr}
  }`;

const fragmentConnection = gql`
  fragment conn on Connection {
    ${requests.model.connQueryStr}
  }`;

// endregion

export default {
  namespaced: false,
  state: {
    doc: {},  // currently open model document; master or release
    master: {},  // master of current open model document
    releases: [],  // releases of master model document
    cache: {
      part: null,
    },

    // query strings
    modelTypename: '',  // model __typename; used to distinguish master vs release model
    queryModel: '',  // query name; used to distinguish master vs release model
    getModelQuery: '',  // apollo query string
    getPartsQuery: '',  // apollo query string
    getConfigsQuery: '',  // apollo query string
  },
  getters: {
    // MODEL
    isRelease: (state) => () => {
      // Check if a model release is loaded in state.
      // To check before model is loaded: this.$route.name === 'openRelease'

      return isDefined(state.doc.version);
    },

    // PARTS
    getPart: () => (Id, fragment=null) => {
      // Read part fragment from Apollo cache.
      // Returns a GraphQL Part type with its complete `obj` fields, unless fragment is specified.
      // PLACEHOLDER {explicitness} refactor usages to make proper use of fragments to query only fields needed

      if (!fragment) fragment = fragmentEntirePart;
      return apolloStore.readFragment('Part', Id, fragment);
    },
    getParts: (state) => (fragment=null) => {
      // Get parts from Apollo cache, excluding ground.
      // Returns a list of GraphQL Part interface type and {obj: {name}} fields, unless fragment is specified.
      // DEV NOTE: Tried using readFragment as an attempt to return only the requested part types, but result is same.

      if (!fragment) fragment = requests.model.fragmentPartObj;
      const query = {
        variables: {Id: state.doc.Id},
        query: gql`query getParts ($Id: ID!) {
          ${state.queryModel} (Id: $Id) {
            parts {
              ${fragment}
            }
          }
        }`,
      };

      // query returns all parts, but only __typename field when fragment does not match
      const { gearsModel: { parts } } = apolloStore.readQuery(query);
      return !fragment ? parts : parts.filter(p => p.Id && !handlers.model.isGround(p.Id));
    },
    getPartsGearDetail: (state, getters) => () => {
      // Syntactic sugar to get detailed gear parts.
      return getters.getParts(requests.model.partGearDetailQueryStr);
    },
    getPartsFrame: (state, getters) => () => {
      // Syntactic sugar to get frame parts.
      return getters.getParts(requests.model.partFrameQueryStr);
    },

    // PARTS FAMILY
    getFrame: (state, getters) => (part) => {
      // Get `frame` part from the Apollo cache.

      return !part.frame ? part.frame : getters.getPart(part.frame.Id);
    },
    getParent: (state, getters) => (part) => {
      // Get `parent` part from the Apollo cache.

      return !part.parent ? part.parent : getters.getPart(part.parent.Id);
    },
    getChildren: (state, getters) => (part) => {
      // Get all parts that reference the specified part as its `parent`.

      const parts = getters.getParts();
      return parts.filter(p => p.parent && p.parent.Id === part.Id);
    },
    getSubframes: (state, getters) => (part) => {
      // Get all parts that reference the specified part as its `frame`.

      // partQueryStr used since feature tree uses geom prop for gear icons
      const parts = getters.getParts(requests.model.partQueryStr);
      return parts.filter(p => p.frame && p.frame.Id === part.Id);
    },
    getCarrier: (state, getters) => (part) => {
      // Recursively get a part's `frame` until its carrier frame is found, if any.

      let partFrame;

      if (!part.frame) {
        partFrame = part.frame;
      } else {
        partFrame = getters.getPart(part.frame.Id);
        if (!handlers.model.isPartFrameCarrier(partFrame)) {
          partFrame = getters.getCarrier(partFrame);
        }
      }

      return partFrame;
    },

    // CONNECTIONS
    getConnection: () => (Id, fragment=null) => {
      // Read connection fragment from Apollo cache.
      // Part fragments include GraphQL Part interface type and {obj: {name}} fields, unless specified otherwise.
      // Returns a GraphQL Connection type, with each part type as mentioned above.

      if (!fragment) {
        fragment = gql`
        fragment connPartObj on Connection {
          Id
          typ
          parts {
            ${requests.model.fragmentPartObj}
          }
        }
      `;
      }

      return apolloStore.readFragment('Connection', Id, fragment);
    },
    getConnections: (state) => (typ, partQueryStr=null) => {
      // Read connections from Apollo cache, filtering by `typ` if specified.
      // Part fragments include GraphQL Part interface type and {obj: {name}} fields, unless specified otherwise.
      // Returns array of GraphQL Connection type, with each part type as mentioned above.

      if (!partQueryStr) {
        partQueryStr = requests.model.fragmentPartObj
      }

      const query = {
        variables: {Id: state.doc.Id},
        query: gql`
          query getConnections ($Id: ID!) {
            ${state.queryModel} (Id: $Id) {
              connections {
                Id
                typ
                parts {
                  ${partQueryStr}
                }
              }
            }
          }`,
      };
      const { gearsModel: { connections } } = apolloStore.readQuery(query);
      return !typ ? connections : connections.filter(c => c.typ === typ);
    },

    // PARTS <--> CONNECTIONS
    getPartConnections: (state, getters) => (part, typ=undefined) => {
      // Get array of connections that include the specified part.
      // NOTE: Works with Ground since it is represented as a frontend dummy part.

      return getters.getConnections(typ).filter(conn => [conn.parts[0].Id, conn.parts[1].Id].includes(part.Id));
    },
    getConnectionsFrom: (state, getters) => (part, typ=undefined) => {
      // Get array of connections that include the specified part.
      // NOTE: Works with Ground since it is represented as a frontend dummy part.

      return getters.getConnections(typ).filter(conn => conn.parts[0].Id === part.Id);
    },
    getConnectedParts: (state, getters) => (part, typ=undefined) => {
      // Get array of parts connected to this part.

      const connections = getters.getPartConnections(part, typ);

      let connectedParts = [];
      let connectedPartId, part1, part2;

      // find the connected part
      connections.forEach(conn => {
        [part1, part2] = conn.parts;
        connectedPartId = [part1.Id, part2.Id].find(Id => Id !== part.Id);
        connectedParts.push(getters.getPart(connectedPartId));
      });

      return connectedParts;
    },
    getConnectedPart: (state, getters) => (part, typ=undefined) => {
      // Arbitrarily get the first part connected to this part.
      // Returns connected part, if any, otherwise nothing.

      const partConnection = getters.getConnections(typ).find(conn => [conn.parts[0].Id, conn.parts[1].Id].includes(part.Id));

      if (partConnection) {
        const connectedPart = partConnection.parts.find(p => p.Id !== part.Id);
        return getters.getPart(connectedPart.Id);
      }
    },

    // DUTY CYCLES
    getDutyCycle: () => (Id, fragment=null) => {
      // Read duty cycle fragment from Apollo cache.
      // Returns a GraphQL DutyCycle type without LoadCase objects, unless fragment specifies otherwise.

      if (!fragment) fragment = requests.model.fragmentDutyCycle;
      return apolloStore.readFragment('DutyCycle', Id, fragment);
    },
    getLoadCase: () => (Id, fragment=null) => {
      // Read load case fragment from Apollo cache.
      // Returns a GraphQL LoadCase type with powerflow but not powerloads, unless fragment specifies otherwise.

      if (!fragment) fragment = requests.model.fragmentLoadCaseTree;
      return apolloStore.readFragment('LoadCase', Id, fragment);
    },
  },
  mutations: {
    SET_DOC(state, doc) {
      state.doc = doc;
    },
    SET_MASTER(state, master) {
      state.master = master;
    },
    SET_QUERY_STRINGS(state) {
      // Set strings used for Apollo queries.
      // It is important that getModelQuery matches the query used in Modeling.vue.

      const isRelease = isDefined(state.doc.version);

      if (!isRelease) {
        state.modelTypename = 'gearsModel';
        state.queryModel = 'gearsModel';
        state.getModelQuery = requests.model.getModel;
      } else {
        state.modelTypename = 'gearsModelRelease';
        state.queryModel = 'gearsModel: gearsModelRelease';
        state.getModelQuery = requests.model.getModelRelease;
      }

      state.getPartsQuery = gql`
        query getParts ($Id: ID!) {
          ${state.queryModel} (Id: $Id) {
            parts {
              ${requests.model.partQueryStr}
            }
          }
        }`;

      // PLACEHOLDER {analysis} change this to be like dutyCycles, queried separate from GearsModel
      //  - Also update all usages of this within cache updates.
      state.getConfigsQuery = gql`
        query getGearConfigs ($Id: ID!) {
          ${state.queryModel} (Id: $Id) {
            Id
            configs {
              ${requests.model.gearsConfigQueryStr}
            }
          }
        }`;
    },

    SET_RELEASES(state, releases) {
      state.releases = releases;
    },
    ADD_RELEASE(state, release) {
      state.releases.push(release);
    },
    UPDATE_RELEASE(state, release) {
      // allows payload object to contain any number of the state fields
      updateObjectById(state.releases, release);
    },
    REMOVE_RELEASE(state, Id) {
      // operates on array instance
      removeObjectById(state.releases, Id);
    },

    SET_PARTS(state, parts) {
      state.parts = parts;
    },
    ADD_PART(state, part) {
      state.parts.push(part);
    },
    REPLACE_PART(state, part) {
      replaceObjectById(state.parts, part);
    },
    UPDATE_PART(state, part) {
      // allows payload object to contain any number of the state fields
      updateObjectById(state.parts, part);
    },
    REMOVE_PART(state, Id) {
      // operates on array instance
      removeObjectById(state.parts, Id);
    },

    UPDATE_PART_CACHE(state, part) {
      // WARNING: Use with caution!
      // Part caching is designed to work with only one part at a time.
      // This technique is used since the 3D model state is a direct reflection of the Apollo cache.
      // This technique allows the Apollo cache to be mutated without API requests, but it complicates state management.

      // read part data from apollo cache
      const data = apolloStore.readFragment('Part', part.Id, fragmentPart);

      // mutate Vuex store to track original part state
      if (!state.cache.part) {
        state.cache.part = copyDeep(data);
      } else if (state.cache.part.Id !== data.Id) {
        // PLACEHOLDER {cache} safety net; revert cache if payload Id is different from cache
        // eslint-disable-next-line no-console
        console.warn('Part cache should be reverted.');
      }

      // mutate apollo cache
      assignProps(data, part);
      apolloStore.writeFragment('Part', part.Id, fragmentPart, data);

      // emit global event
      EventBus.$emit('update-part', data);
    },
    REVERT_PART_CACHE(state) {
      // Restore apollo cache for part.

      // get Vuex cache
      const part = state.cache.part;
      if (!part) return;

      // read part data from apollo cache
      const data = apolloStore.readFragment('Part', part.Id, fragmentPart);

      // mutate apollo cache
      assignProps(data, part);
      apolloStore.writeFragment('Part', part.Id, fragmentPart, data);

      // emit global event
      EventBus.$emit('update-part', data);

      // clear Vuex cache
      state.cache.part = null;
    },
    CLEAR_PART_CACHE(state) {
      // Clear Vuex cache of part.
      state.cache.part = null;
    },

    SET_CONNECTIONS(state, connections) {
      state.connections = connections;
    },
    ADD_CONNECTION(state, connection) {
      state.connections.push(connection);
    },
    UPDATE_CONNECTION(state, connection) {
      // allows payload object to contain any number of the state fields
      updateObjectById(state.connections, connection)
    },
    REMOVE_CONNECTION(state, Id) {
      // operates on array instance
      removeObjectById(state.connections, Id);
    },
  },
  actions: {
    // MODEL
    async createModel() {
      // API mutation to create a new model.
      // Action to open model in the modeling environment is NOT performed here.
      // On success, returns the new model Id field.

      const mutate = {
        variables: {
          data: {
            name: 'Untitled model',
          }
        },
        mutation: gql`mutation createModel ($data: GearsModelInput!) {
            createGearsModel (data: $data) {
              gearsModel {
                Id
              }
            }
          }`,
      };
      const {data: {createGearsModel: {gearsModel}}} = await apolloMutate(mutate);
      return gearsModel.Id;
    },
    async updateModel({ commit }, payload) {
      // API mutation to update model fields.
      // payload should include only fields to update, plus Id field of model to update.
      // Updating embedded document fields is not supported, due to query string.
      // PLACEHOLDER {performance} implement optimisticResponse in mutate

      commit('SET_STATUS', {message: 'Updating model'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateModel ($data: UpdateGearsModelInput!) {
            updateGearsModel (data: $data) {
              gearsModel {
                ${apolloFieldsString(Object.keys(payload))}
                lastModifiedBy
                lastModifiedOn
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));

      // DEPRECATED - mutate global state; see Modeling.vue
      // commit('UPDATE_DOC', gearsModel);
      // commit('UPDATE_MASTER', gearsModel);
    },

    // RELEASE
    async createRelease({ state, commit }, { version, notes }) {
      // API mutation to create a new model release.
      // payload must include `version` and `notes` properties

      commit('SET_STATUS', {message: 'Creating model release'});

      const mutate = {
        variables: {
          data: {
            Id: state.doc.Id,
            version: version,
            notes: notes,
          }
        },
        mutation: gql`mutation createModelRelease ($data: ReleaseInput!) {
            createGearsModelRelease (data: $data) {
              gearsModelRelease {
                ${requests.model.modelReleaseFields}
              }
            }
          }`,
      };
      const {data: {createGearsModelRelease: {gearsModelRelease}}} = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      // mutate global state
      commit('ADD_RELEASE', gearsModelRelease);

      // notify user
      Notification.success({
        position: defaults.notifyPosition,
        title: 'Model Release',
        message: 'Version: ' + gearsModelRelease.version,
      });
    },
    async updateRelease({ commit }, payload) {
      // API mutation to update model release fields.
      // payload should include only fields to update, plus Id field of model release to update/
      // Updating embedded document fields is not supported, due to query string.
      // PLACEHOLDER {performance} implement optimisticResponse in mutate

      commit('SET_STATUS', {message: 'Updating release'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateModelRelease ($data: UpdateGearsModelReleaseInput!) {
            updateGearsModelRelease (data: $data) {
              gearsModelRelease {
                ${apolloFieldsString(Object.keys(payload))}
                lastModifiedBy
                lastModifiedOn
              }
            }
          }`,
      };
      const {data: {updateGearsModelRelease: {gearsModelRelease}}} = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      // mutate global state; in the case that state.doc is a release, see Modeling.vue for its update mutation
      commit('UPDATE_RELEASE', gearsModelRelease);
    },
    async deleteRelease({ state, commit }, releaseId) {
      // API mutation to delete a model release.
      // Returns boolean to indicate success of deletion.

      commit('SET_STATUS', {message: 'Deleting model release'});
      const { version } = getObjectById(state.releases, releaseId);

      // mutate DB
      const mutate = {
        variables: {
          Id: releaseId,
        },
        mutation: gql`mutation deleteModelRelease ($Id: ID!) {
            deleteGearsModelRelease (Id: $Id) {
              success
            }
          }`,
      };
      const {data: {deleteGearsModelRelease: {success}}} = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      // mutate global state
      if (success) {
        commit('REMOVE_RELEASE', releaseId);

        Notification.success({
          position: defaults.notifyPosition,
          title: 'Deleted Version',
          message: version,
        });
      } else {
        Message.error({
          center: true,
          message: 'Delete operation failed!  Are you sure the model release still exists?',
        });
      }

      return success;
    },

    // PARTS
    async addPart({ state, commit }, { obj, typ }) {
      // API mutation to add new part to current model.
      // Payload `obj` is the library object, not its Id, to reference.
      // Part name defaults to the library object name.
      // Returns part added to model.

      commit('SET_STATUS', {message: 'Adding part'});

      // optional fields
      const name = obj.name;
      const objId = obj ? obj.Id : null;

      // mutate DB
      const mutate = {
        variables: {
          data: {
            model: state.doc.Id,
            name: name,
            obj: objId,
          },
          typ: typ,
        },
        mutation: gql`mutation addPart($data: PartInput!, $typ: PartType!) {
            addPart(data: $data, typ: $typ) {
              part {
                ${requests.model.getPartQueryStr(typ)}
              }
            }
          }`,
        update: (cache, { data: { addPart: { part } } }) => {
          // Update cache of collection to avoid a network refetch.

          const query = {
            variables: { Id: state.doc.Id },
            query: state.getModelQuery,
          };

          // Read the data from our cache for this query.
          const data = cache.readQuery(query);
          // Modify the query data object.
          data.gearsModel.parts.push(part);
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data });
        },
      };
      const {data: {addPart: {part}}} = await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));

      // DEPRECATED - mutate global state; parts handled by Modeling.vue
      // commit('ADD_PART', part);

      // trigger global event
      EventBus.$emit('add-part', part);

      Notification.success({
        position: defaults.notifyPosition,
        title: 'New Part',
        message: `${part.obj.name} added to model`,
      });

      return part;
    },
    async addPartFrame({ dispatch }, payloadLib) {
      // API mutation to add new part to current model.
      // Payload is for the library object.
      // Returns part added to model.

      // create lib object
      const frame = await dispatch('createFrame', payloadLib);

      // add part to model; if request fails, then lib object is deleted
      return await dispatch('addPart', {obj: frame, typ: requests.model.PartType.FRAME})
        .catch(async () => await dispatch('deleteFrame', frame.Id))
    },
    async updatePart({ getters, commit }, part) {
      // API mutation to update a part.
      // Payload must be object of UpdatePartInput type per GraphQL API.
      // Apollo automatically handles the cache update.
      // Returns boolean to indicate a successful mutation.

      commit('SET_STATUS', {message: 'Updating part'});

      // set Apollo fragment; viewer props or entire part
      const nonViewerFields = Object.keys(part).filter(k => k !== 'viewer');
      const isViewerOnly = nonViewerFields.length === 1;  // Id is only non-viewer field
      const inlineFragment = isViewerOnly ? inlineFragmentViewer : requests.model.fragmentPart;
      const fragment = isViewerOnly ? fragmentViewer : fragmentPart;

      // optimistic response; verified that cache is not mutated
      const optimisticPart = getters.getPart(part.Id, fragment);
      const optimisticPayload = {...part};

      // if updating parent, then define an optimistic parent
      if (part.parent) {
        const parentPart = apolloStore.readFragment('Part', part.parent, fragment);
        optimisticPayload.parent = {Id: parentPart.Id, __typename: parentPart.__typename};
      } else if (part.parent === null) {
        optimisticPayload.parent = null;
      }

      // if updating frame, then define an optimistic frame
      if (part.frame) {
        const framePart = apolloStore.readFragment('Part', part.frame, fragment);
        optimisticPayload.frame = {Id: framePart.Id, __typename: framePart.__typename};
      } else if (part.frame === null) {
        optimisticPayload.frame = null;
      }

      updateObject(optimisticPart, optimisticPayload);

      // mutate DB; queries only Part interface, not lib obj fields
      let updated = true;
      const mutate = {
        variables: { data: part },
        mutation: gql`mutation updatePart($data: UpdatePartInput!) {
          updatePart(data: $data) {
            part {
              ${inlineFragment}
            }
          }
        }`,
        optimisticResponse: {
          __typename: 'Mutation',
          updatePart: {
            __typename: 'UpdatePart',
            part: optimisticPart,
          },
        },
        update: (cache, { data: { updatePart } }) => {
          // trigger global event based on optimistic and actual response
          // on server error: update hook called with optimistic response, then cache rolls back to original state
          // on server error: update hook is *not* get called with rolled back cache

          EventBus.$emit('update-part', updatePart.part);
        }
      };
      await apolloMutate(mutate)
        .then(() => commit('CLEAR_PART_CACHE'))
        .catch(() => {
          // undo optimistic global event
          commit('REVERT_PART_CACHE');
          const partToUpdate = getters.getPart(part.Id);
          EventBus.$emit('update-part', partToUpdate);
          updated = false;
        })
        .finally(() => commit('CLEAR_STATUS'));

      // DEPRECATED - mutate global state; parts handled by Modeling.vue
      // commit('UPDATE_PART', updatePart.part);

      return updated;
    },
    async removePart({ state, getters, commit, dispatch }, part) {
      // Remove parts from the model.
      // Returns boolean to indicate if removal was successful.
      // PLACEHOLDER {performance} use Apollo optimistic result option for snappy UI

      const Id = part.Id;
      const subframes = getters.getSubframes(part);
      const message = subframes.length === 0 ? 'Delete part from model?' : 'Delete entire subassembly?';
      const title = handlers.model.partLabel(part);

      MessageBox.confirm(message, title, {
        type: 'error',
        showClose: false,
        showCancelButton: true,
        showConfirmButton: true,
      }).then(async () => {
        // confirmed part removal; proceed with mutation
        commit('SET_STATUS', {message: 'Removing part'});
        const partToRemove = getters.getPart(Id);

        // cascade delete rules
        function cascadeDelete(part, gearsModel) {
          // Cascade delete part to remove dependent parts and connections.
          // WARNING: Recommended for use with *optimistic response* only since data model behavior is assumed here.
          // Returns array of deleted parts.

          function findSubframes(part, arrParts) {
            // Recursively find subframe parts of the specified part.
            // Returns flat array of subframe parts.

            // find subframes of part
            let subframes = arrParts.filter(p => p.frame && p.frame.Id === part.Id);

            // DEV ONLY
            // console.log('subframes1', subframes);

            // find subframes of the subframes
            subframes.forEach(subframe => {
              const subsubframes = findSubframes(subframe, arrParts);
              subframes.push(subsubframes);
            });

            // DEV ONLY
            // console.log('subframes2', subframes);

            return subframes.flat();
          }

          // delete input part
          removeObjectById(gearsModel.parts, part.Id);

          // delete each subframe Part of the input part
          let deletedParts = [part];
          findSubframes(part, gearsModel.parts).forEach(subframe => {
            removeObjectById(gearsModel.parts, subframe.Id);
            deletedParts.push(subframe);
          });

          // delete each Connection referencing a deleted part
          deletedParts.forEach(delPart => {
            getters.getPartConnections(delPart).forEach(conn => {
              removeObjectById(gearsModel.connections, conn.Id);
            });
          });

          return deletedParts;
        }

        // nullify delete rules
        function nullifyDelete(deletedParts, gearsModel) {
          // Nullify fields referencing deleted parts.  gearsModel.parts is mutated.
          // WARNING: Recommended for use with *optimistic response* only since data model behavior is assumed here.
          // Returns updated parts.

          let updatedObject, updatedParts = [];

          // nullify Part.parent
          deletedParts.forEach(delPart => {
            getters.getChildren(delPart).forEach(child => {
              try {
                updatedObject = updateObjectById(gearsModel.parts, {Id: child.Id, parent: null});
                updatedParts.push(updatedObject);
              } catch (e) {
                // child to update is undefined because it is a deleted part
              }
            });
          });

          return updatedParts;
        }

        // mutate DB
        const mutate = {
          variables: {
            Id: Id,
          },
          mutation: gql`mutation removePart($Id: ID!) {
            removePart(Id: $Id) {
              success
            }
          }`,
          optimisticResponse: {
            __typename: 'Mutation',
            removePart: {
              __typename: 'RemovePart',
              success: true,
            },
          },
          update: (cache, {data: {removePart: {success}}}) => {
            // Update cache with optimistic response, then actual response.
            // on server error: update hook called with optimistic response, then cache rolls back to original state
            // on server error: update hook is *not* get called with rolled back cache
            // PLACEHOLDER {robustness} update cache per API response of remaining parts and connections

            if (success) {
              // Read the data from our cache for this query.
              const query = {
                variables: {Id: state.doc.Id},
                query: state.getModelQuery,
              };
              const {gearsModel} = cache.readQuery(query);

              // Modify the query data object.
              //  1. Cascade deletion of part, thereby deleting dependent parts and connections.
              const deletedParts = cascadeDelete(partToRemove, gearsModel);
              //  2. Nullify part fields referencing the deleted parts.
              const updatedParts = nullifyDelete(deletedParts, gearsModel);

              // Write modified data back to the cache.
              cache.writeQuery({...query, data: {gearsModel}});

              // Trigger global events based on optimistic and actual response.
              EventBus.$emit('remove-parts', deletedParts.map(p => p.Id));
              updatedParts.forEach(updatedPart => EventBus.$emit('update-part', updatedPart));

              // clear any selections
              dispatch('clearUI');
            }
          },
        };
        const {data: {removePart: {success}}} = await apolloMutate(mutate)
          .catch(() => {
            // undo any optimistic global events
            EventBus.$emit('redraw-scene');
          })
          .finally(() => commit('CLEAR_STATUS'));

        // DEPRECATED - mutate global state; parts handled by Modeling.vue
        // if (isRemoved) {
        //   // 1. Remove deleted part.
        //   commit('REMOVE_PART', Id);
        //   // 2. Remove deleted connections.
        //   connectionsRemoved.forEach(conn => {
        //     commit('REMOVE_CONNECTION', conn.Id);
        //   });
        //   // 3. Replace updated parts.
        //   partsUpdated.forEach(part => {
        //     commit('REPLACE_PART', part);
        //   });
        // }

        return success;
      }).catch(() => {/*do nothing*/});
    },
    // ---
    async updateParts({ commit }, parts) {
      // API mutation to update multiple parts in current model.
      // Payload must be an array of UpdatePartInput objects per GraphQL API.
      // Apollo automatically handles the cache update.
      // No return value.
      // PLACEHOLDER {batch} look into proper batching techniques with Apollo
      // PLACEHOLDER {performance} implement optimisticResponse in mutate

      if (parts.length === 0) return [];
      commit('SET_STATUS', {message: 'Updating parts'});

      // function to synthesize query string; does not fetch lib obj fields
      function updatePart(i) {
        return `
          update${i}: updatePart(data: $data${i}) {
            part {
              ${requests.model.fragmentPart}
            }
          }\n`;
      }

      // synthesize variables, input string, and query string
      let variables = {};
      let updatePartsVariables = ``;
      let updatePartsQueryStr = ``;
      parts.forEach((part, index) => {
        variables[`data${index}`] = part;
        updatePartsVariables += `$data${index}: UpdatePartInput!\n`;
        updatePartsQueryStr += updatePart(index);
      });

      // mutate DB
      const mutate = {
        variables: variables,
        mutation: gql`mutation updateParts(${updatePartsVariables}) {
            ${updatePartsQueryStr}
          }`,
      };
      const { data } = await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
      const updateParts = Object.values(data);

      // DEPRECATED - mutate global state; parts handled by Modeling.vue
      // updateParts.forEach(updatePart => commit('UPDATE_PART', updatePart.part));

      // trigger global event
      updateParts.forEach(updatePart => {
        EventBus.$emit('update-part', updatePart.part);
      });
    },

    // CONNECTIONS
    async addConnection({ state, getters, commit }, { part1, part2, typ }) {
      // API mutation to add new connection to current model.
      // Payload must be object of ConnectionInput type per GraphQL API, except `model` field.
      // Returns connection added to model.

      commit('SET_STATUS', {message: 'Adding constraint'});

      // update payload to consider ground
      part1 = handlers.model.isGround(part1) ? null : part1;
      part2 = handlers.model.isGround(part2) ? null : part2;

      // mutate DB
      const mutate = {
        variables: {
          data: {
            model: state.doc.Id,
            part1: part1,
            part2: part2,
            typ: typ,
          },
        },
        mutation: gql`mutation addConnection($data: ConnectionInput!) {
            addConnection(data: $data) {
              connection {
                ${requests.model.connQueryStr}
              }
            }
          }`,
        update: (cache, { data: { addConnection: { connection } } }) => {
          // Update cache of collection to avoid a network refetch.

          const query = {
            variables: { Id: state.doc.Id },
            query: state.getModelQuery,
          };

          // clean connection object
          requests.model.cleanConnection(connection);

          // Read the data from our cache for this query.
          const data = cache.readQuery(query);
          // Modify the query data object.
          data.gearsModel.connections.push(connection);
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data })
        },
      };
      const { data: { addConnection: { connection } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      // DEPRECATED - mutate global state; connections handled by Modeling.vue
      // commit('ADD_CONNECTION', connection);

      // trigger global event
      EventBus.$emit('add-connection', connection);

      // DEPRECATED - notify user
      // Notification.success({
      //   position: defaults.notifyPosition,
      //   title: `New ${handlers.model.connTypeLabel(connection)}`,
      //   message: handlers.model.connLabel(connection),
      // });

      // select connection
      commit('SET_SELECTED_CONNECTIONS', [getters.getConnection(connection.Id)]);

      return connection;
    },
    async updateConnection({ state, getters, commit }, conn) {
      // API mutation to update a connection.
      // Payload must be object of UpdateConnectionInput type per GraphQL API.
      // Apollo automatically handles the cache update.
      // No return value.

      commit('SET_STATUS', {message: 'Updating constraint'});

      // helper fcn
      const getOptimisticPart = (partId) => {
        // Synthesize an optimistic connection part.
        // IMPORTANT: partId expected as null for ground.

        if (partId) {
          const part = apolloStore.readFragment('Part', partId, fragmentPart);
          return {Id: part.Id, __typename: part.__typename};
        } else {
          return null;
        }
      };

      // optimistic response; verified that cache is not mutated
      const optimisticConnection = getters.getConnection(conn.Id, fragmentConnection);
      const optimisticPayload = {...conn};

      // if mutating parts, define their optimistic response
      if (conn.part1 || conn.part2) {
        // update payload to consider ground
        conn.part1 = handlers.model.isGround(conn.part1) ? null : conn.part1;
        conn.part2 = handlers.model.isGround(conn.part2) ? null : conn.part2;

        delete optimisticPayload.part1;
        delete optimisticPayload.part2;

        optimisticPayload.parts = [];
        optimisticPayload.parts[0] = getOptimisticPart(conn.part1);
        optimisticPayload.parts[1] = getOptimisticPart(conn.part2);
      }
      updateObject(optimisticConnection, optimisticPayload);

      // mutate DB
      const mutate = {
        variables: { data: conn },
        mutation: gql`mutation updateConnection($data: UpdateConnectionInput!) {
          updateConnection(data: $data) {
            connection {
              ${requests.model.connQueryStr}
            }
          }
        }`,
        optimisticResponse: {
          __typename: 'Mutation',
          updateConnection: {
            __typename: 'UpdateConnection',
            connection: optimisticConnection,
          },
        },
        update: (cache, { data: { updateConnection: {connection} } }) => {
          // Apollo automatically updates cache, but cache is mutated here to clean any connections to ground.
          // mutate global state based on optimistic and actual response
          // on server error: update hook called with optimistic response, then cache rolls back to original state
          // on server error: update hook is *not* get called with rolled back cache

          const query = {
            variables: { Id: state.doc.Id },
            query: state.getModelQuery,
          };

          // clean connection object
          requests.model.cleanConnection(connection);

          // Read the data from our cache for this query.
          const data = cache.readQuery(query);
          // Modify the query data object.
          replaceObjectById(data.gearsModel.connections, connection);
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data });
        }
      };
      await apolloMutate(mutate)
        .finally(() => {
          commit('CLEAR_STATUS');
          commit('SET_SELECTED_CONNECTIONS', [getters.getConnection(conn.Id)]);
        });
    },
    async _removeConnection({ state, commit, dispatch }, Id) {
      // API mutation to remove connection from current model.
      // Returns boolean to indicate if removal was successful.

      commit('SET_STATUS', {message: 'Removing constraint'});

      // mutate DB
      const mutate = {
        variables: {
          Id: Id,
        },
        mutation: gql`mutation removeConnection($Id: ID!) {
            removeConnection(Id: $Id) {
              success
            }
          }`,
        optimisticResponse: {
          __typename: 'Mutation',
          removeConnection: {
            __typename: 'RemoveConnection',
            success: true,
          },
        },
        update: (cache, { data: { removeConnection: { success } } }) => {
          // Update cache of collection to avoid a network refetch.

          if (success) {
            const query = {
              variables: { Id: state.doc.Id },
              query: state.getModelQuery,
            };

            // Read the data from our cache for this query.
            const data = cache.readQuery(query);
            // Modify the query data object.
            // Remove deleted connection.
            removeObjectById(data.gearsModel.connections, Id);
            // Write modified data back to the cache.
            cache.writeQuery({ ...query, data });

            // clear any selections
            dispatch('clearUI');
          }
        },
      };
      const { data: { removeConnection: { success } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      // DEPRECATED - mutate global state; connections handled by Modeling.vue
      // if (success) commit('REMOVE_CONNECTION', Id);

      // trigger global event
      EventBus.$emit('remove-connection', Id);

      return success;
    },
    async removeConnection({ dispatch }, conn) {
      // API mutation to remove connection from current model.
      // Only prompts confirmation for powerload connection type.
      // Returns boolean to indicate if removal was successful.

      if (conn.typ === requests.model.ConnectionType.POWERLOAD) {
        return MessageBox.confirm('Delete powerload from model?', 'Powerload', {
          type: 'error',
          showClose: false,
          showCancelButton: true,
          showConfirmButton: true,
        })
          .then(async () => {return await dispatch('_removeConnection', conn.Id)})
          .catch(() => {/*do nothing*/})
      } else {
        return await dispatch('_removeConnection', conn.Id);
      }
    },
    // ---
    async addConnections({ state, commit }, connections) {
      // API mutation to add multiple new connections to current model.
      // Payload must be an array of ConnectionInput objects per GraphQL API, except `model` field.
      // {Array} connections - Array of objects: [{{String} typ, {String} part1, {String} part2}, ...]
      // Returns array of added connections.

      if (connections.length === 0) return [];
      commit('SET_STATUS', {message: 'Adding constraints'});

      // function to synthesize query string
      function addConnection(i) {
        return `
          add${i}: addConnection(data: $data${i}) {
            connection {
              ${requests.model.connQueryStr}
            }
          }\n`;
      }

      // synthesize variables, input string, and query string
      let variables = {};
      let addConnectionsVariables = ``;
      let addConnectionsQueryStr = ``;
      connections.forEach((conn, index) => {
        conn.model = state.doc.Id;
        variables[`data${index}`] = conn;
        addConnectionsVariables += `$data${index}: ConnectionInput!\n`;
        addConnectionsQueryStr += addConnection(index);
      });

      const mutate = {
        variables: variables,
        mutation: gql`mutation addConnections(${addConnectionsVariables}) {
            ${addConnectionsQueryStr}
          }`,
        update: (cache, { data }) => {
          // Update cache of collection to avoid a network refetch.

          const query = {
            variables: { Id: state.doc.Id },
            query: state.getModelQuery,
          };

          // Read the data from our cache for this query.
          const dataQuery = cache.readQuery(query);
          // Modify the query data object.
          Object.values(data).forEach(addConnection => {
            dataQuery.gearsModel.connections.push(addConnection.connection);
          });
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data: dataQuery })
        },
      };
      const { data } = await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
      const addConnections = Object.values(data);
      const newConnections = addConnections.map(addConnection => {
        // trigger global event
        EventBus.$emit('add-connection', addConnection.connection);

        return addConnection.connection;
      });

      // DEPRECATED - mutate global state; connections handled by Modeling.vue
      // newConnections.forEach(conn => commit('ADD_CONNECTION', conn));

      // notify user
      Notification.success({
        position: defaults.notifyPosition,
        title: 'New Connections',
        message: `${newConnections.length} connections added`,
      });

      return newConnections;
    },
    async updateConnections({ commit }, connections) {
      // API mutation to update multiple connections in current model.
      // Payload must be an array of UpdateConnectionInput objects per GraphQL API, except `model` field.
      // {Array} connections - Array of objects: [{{String} Id, {String} typ, {String} part1, {String} part2}, ...]
      // Apollo automatically handles the cache update.
      // No return value.
      // PLACEHOLDER {batch} look into proper batching techniques with Apollo

      if (connections.length === 0) return [];
      commit('SET_STATUS', {message: 'Updating constraints'});

      // function to synthesize query string
      function updateConnection(i) {
        return `
          update${i}: updateConnection(data: $data${i}) {
            connection {
              ${requests.model.connQueryStr}
            }
          }\n`;
      }

      // synthesize variables, input string, and query string
      let variables = {};
      let updateConnectionsVariables = ``;
      let updateConnectionsQueryStr = ``;
      connections.forEach((conn, index) => {
        // PLACEHOLDER {conn updates} `model` field removed from UpdateConnectionInput in backend; verify it works
        // conn.model = state.doc.Id;
        variables[`data${index}`] = conn;
        updateConnectionsVariables += `$data${index}: UpdateConnectionInput!\n`;
        updateConnectionsQueryStr += updateConnection(index);
      });

      // mutate DB
      const mutate = {
        variables: variables,
        mutation: gql`mutation updateConnections(${updateConnectionsVariables}) {
            ${updateConnectionsQueryStr}
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));

      // DEPRECATED - mutate global state; connections handled by Modeling.vue
      // const updateConnections = Object.values(data);
      // updateConnections.forEach(updateConnection => commit('UPDATE_CONNECTION', updateConnection.connection));
    },
    async removeConnections({ state, commit }, arrId) {
      // API mutation to remove multiple new connections from current model.
      // Returns boolean array to indicate if removal was successful for each connection.
      // PLACEHOLDER {batch} look into proper batching techniques with Apollo

      if (arrId.length === 0) return [];
      commit('SET_STATUS', {message: 'Removing constraints'});

      // function to synthesize query string
      function removeConnection(i) {
        return `
          remove${i}: removeConnection(Id: $Id${i}) {
            success
          }\n`;
      }

      // synthesize variables, input string, and query string
      let variables = {};
      let removeConnectionsVariables = ``;
      let removeConnectionsQueryStr = ``;
      arrId.forEach((Id, index) => {
        variables[`Id${index}`] = Id;
        removeConnectionsVariables += `$Id${index}: ID!\n`;
        removeConnectionsQueryStr += removeConnection(index);
      });

      // mutate DB
      const mutate = {
        variables: variables,
        mutation: gql`mutation removeConnections(${removeConnectionsVariables}) {
            ${removeConnectionsQueryStr}
          }`,
        update: (cache, { data }) => {
          // Update cache of collection to avoid a network refetch.

          const query = {
            variables: { Id: state.doc.Id },
            query: state.getModelQuery,
          };

          // Read the data from our cache for this query.
          const dataQuery = cache.readQuery(query);
          // Modify the query data object.
          Object.values(data).forEach((removeConnection, i) => {
            if (removeConnection.success) {
              removeObjectById(dataQuery.gearsModel.connections, arrId[i]);
            }
          });
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data: dataQuery })
        },
      };
      const { data } = await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
      const arrRemoveConnections = Object.values(data);

      // DEPRECATED - mutate global state; connections handled by Modeling.vue
      // arrRemoveConnections.forEach((removeConnection, i) => {
      //   if (removeConnection.success) {
      //     commit('REMOVE_CONNECTION', arrId[i])
      //   }
      // });

      // notify user
      const countTotal = arrRemoveConnections.reduce((accumulator, removeConnection) => {
        const count = removeConnection.success ? 1 : 0;
        return accumulator + count;
      }, 0);
      Notification.success({
        position: defaults.notifyPosition,
        title: 'Delete Connections',
        message: `${countTotal} connections removed`,
      });

      return arrRemoveConnections.map(removeConnection => removeConnection.success);
    },

    // CONFIG
    async getGearsConfig({ state }) {
      // API query to get model configs; uses cache except first query.

      const query = {
        fetchPolicy: "cache-first",
        variables: {Id: state.doc.Id},
        query: state.getConfigsQuery,
      };

      const {data: {gearsModel: {configs}}} = await apolloQuery(query);
      return configs;
    },
    async createGearsConfig({ state, commit }, {name, clutched}) {
      // API mutation to add config to current model.
      // Payload must be object of GearsConfigInput type per GraphQL API, except `model` field.
      // Returns config added to model.

      commit('SET_STATUS', {message: 'Adding configuration'});

      // mutate DB
      const mutate = {
        variables: {
          data: {
            model: state.doc.Id,
            name: name,
            clutched: clutched,
          },
        },
        mutation: gql`mutation createGearsConfig($data: GearsConfigInput!) {
            createGearsConfig(data: $data) {
              gearsConfig {
                ${requests.model.gearsConfigQueryStr}
              }
            }
          }`,
        update: (cache, { data: { createGearsConfig: { gearsConfig } } }) => {
          // Update cache of collection to avoid a network refetch.

          const query = {
            variables: { Id: state.doc.Id },
            query: state.getConfigsQuery,
          };

          // Read the data from our cache for this query.
          const data = cache.readQuery(query);
          // Modify the query data object.
          data.gearsModel.configs.push(gearsConfig);
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data })
        },
      };
      const { data: { createGearsConfig: { gearsConfig } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return gearsConfig;
    },
    async replaceGearsConfigs({ state, commit }, configs) {
      // API mutation to replace multiple configs in current model.
      // Payload must be an array of GearsConfigInput objects per API, including `Id` but not `model` field.
      // Apollo automatically handles the cache update.
      // No return value.
      // PLACEHOLDER {batch} look into proper batching techniques with Apollo

      if (configs.length === 0) return;
      commit('SET_STATUS', {message: 'Updating configurations'});

      // function to synthesize query string
      function replaceGearsConfig(i) {
        return `
          replace${i}: createGearsConfig(data: $data${i}) {
            gearsConfig {
              ${requests.model.gearsConfigQueryStr}
            }
          }\n`;
      }

      // synthesize variables, input string, and query string
      let variables = {};
      let replaceConfigsVariables = ``;
      let replaceConfigsQueryStr = ``;
      configs.forEach((config, index) => {
        variables[`data${index}`] = {...config, model: state.doc.Id};
        replaceConfigsVariables += `$data${index}: GearsConfigInput!\n`;
        replaceConfigsQueryStr += replaceGearsConfig(index);
      });

      // mutate DB
      const mutate = {
        variables: variables,
        mutation: gql`mutation replaceGearsConfigs(${replaceConfigsVariables}) {
            ${replaceConfigsQueryStr}
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
    async removeGearsConfig({ state, commit, dispatch }, Id) {
      // API mutation to remove config from current model.
      // Returns boolean to indicate if removal was successful.

      commit('SET_STATUS', {message: 'Removing configuration'});

      // mutate DB
      const mutate = {
        variables: {
          Id: Id,
        },
        mutation: gql`mutation removeGearsConfig($Id: ID!) {
            removeGearsConfig(Id: $Id) {
              success
            }
          }`,
        optimisticResponse: {
          __typename: 'Mutation',
          removeGearsConfig: {
            __typename: 'RemoveGearsConfig',
            success: true,
          },
        },
        update: (cache, { data: { removeGearsConfig: { success } } }) => {
          // Update cache of collection to avoid a network refetch.

          if (success) {
            const query = {
              variables: { Id: state.doc.Id },
              query: state.getConfigsQuery,
            };

            // Read the data from our cache for this query.
            const data = cache.readQuery(query);
            // Modify the query data object.
            // Remove deleted config.
            removeObjectById(data.gearsModel.configs, Id);
            // Write modified data back to the cache.
            cache.writeQuery({ ...query, data });

            // clear any selections
            dispatch('clearUI');
          }
        },
      };
      const { data: { removeGearsConfig: { success } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return success;
    },

    // DUTY CYCLES
    async createDutyCycle({ state, commit }, {name, notes}) {
      // API mutation to add duty cycle to current model.
      // Payload must be object of DutyCycleInput type per GraphQL API, except `model` field.
      // Returns duty cycle added to model.

      commit('SET_STATUS', {message: 'Adding duty cycle'});

      // mutate DB
      const mutate = {
        variables: {
          data: {
            model: state.doc.Id,
            name,
            notes,
          },
        },
        mutation: gql`mutation createDutyCycle ($data: DutyCycleInput!) {
            createDutyCycle (data: $data) {
              dutyCycle {
                ${requests.model.dutyCycleTreeQueryStr}
              }
            }
          }`,
        optimisticResponse: {
          __typename: 'Mutation',
          createDutyCycle: {
            __typename: 'CreateDutyCycle',
            dutyCycle: {
              __typename: 'DutyCycle',
              Id: Date.now().toString(),
              name,
              notes,
              loadCases: [],
            },
          },
        },
        update: (cache, { data: { createDutyCycle: { dutyCycle } } }) => {
          // Update cache of collection to avoid a network refetch.

          const query = {
            variables: { model: state.doc.Id },
            query: requests.model.getDutyCycles,
          };

          // Read the data from our cache for this query.
          const data = cache.readQuery(query);
          // Modify the query data object.
          data.dutyCycles.push(dutyCycle);
          // Write modified data back to the cache.
          cache.writeQuery({ ...query, data })
        },
      };
      const { data: { createDutyCycle: { dutyCycle } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return dutyCycle;
    },
    async updateDutyCycle({ commit }, payload) {
      // API mutation to update a duty cycle (not its load cases).
      // Payload must be object of UpdateDutyCycleInput type per GraphQL API.
      // Apollo automatically handles the cache update.
      // No return value.

      commit('SET_STATUS', {message: 'Updating duty cycle'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateDutyCycle ($data: UpdateDutyCycleInput!) {
            updateDutyCycle (data: $data) {
              dutyCycle {
                ${apolloFieldsString(Object.keys(payload))}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
    async removeDutyCycle({ state, commit, dispatch }, Id) {
      // API mutation to remove duty cycle from current model.
      // WARNING: Cascade deletes all load cases of duty cycle.
      // Returns boolean to indicate if removal was successful.

      MessageBox.confirm('Delete duty cycle?  This operation cannot be undone.', '', {
        type: 'error',
        showClose: false,
        showCancelButton: true,
        showConfirmButton: true,
      }).then(async () => {
        commit('SET_STATUS', {message: 'Removing duty cycle'});

        // mutate DB
        const mutate = {
          variables: {
            Id: Id,
          },
          mutation: gql`mutation removeDutyCycle($Id: ID!) {
            removeDutyCycle(Id: $Id) {
              success
            }
          }`,
          optimisticResponse: {
            __typename: 'Mutation',
            removeDutyCycle: {
              __typename: 'RemoveDutyCycle',
              success: true,
            },
          },
          update: (cache, { data: { removeDutyCycle: { success } } }) => {
            // Update cache of collection to avoid a network refetch.

            if (success) {
              const query = {
                variables: { model: state.doc.Id },
                query: requests.model.getDutyCycles,
              };

              // Read the data from our cache for this query.
              const data = cache.readQuery(query);
              // Modify the query data object.
              // Remove deleted duty cycle.
              removeObjectById(data.dutyCycles, Id);
              // Write modified data back to the cache.
              cache.writeQuery({ ...query, data });

              // clear any selections
              dispatch('clearUI');
            }
          },
        };
        const { data: { removeDutyCycle: { success } } } = await apolloMutate(mutate)
          .finally(() => commit('CLEAR_STATUS'));

        return success;
      })
        .catch(() => {/*do nothing*/});
    },

    // LOAD CASES
    async hasPowerflow({ commit }, Id) {
      // API query to check if load case has a valid powerflow, i.e. unexpired; uses network-only.

      commit('SET_STATUS', {message: 'Validating load case'});

      const query = {
        fetchPolicy: "network-only",
        variables: {Id},
        query: gql`
          query getLoadCase ($Id: ID!) {
            loadCase (Id: $Id) {
              hasPowerflow
            }
          }`,
      };

      const {data: {loadCase: {hasPowerflow}}} = await apolloQuery(query)
        .finally(() => commit('CLEAR_STATUS'));

      return hasPowerflow;
    },
    async getLoadCases(_, {dutyCycle, Ids}) {
      // API query to get a specific set of load cases, including powerloads & results.
      // @param dutyCycle - ID of duty cycle.
      // @param Ids - array of load case IDs.

      const query = {
        fetchPolicy: "network-only",
        variables: {dutyCycle, Ids},
        query: gql`
          query getLoadCases ($dutyCycle: ID!, $Ids: [ID!]) {
            loadCases (dutyCycle: $dutyCycle, Ids: $Ids) {
              ${requests.model.loadCaseFullQueryStr}
            }
          }`,
      };

      const {data: {loadCases}} = await apolloQuery(query);
      return loadCases;
    },
    async createLoadCase({ commit }, {dutyCycle, name, duration}) {
      // API mutation to add load case to current model.
      // Payload must be object of LoadCaseInput type per GraphQL API.
      // Returns load case added to model.

      commit('SET_STATUS', {message: 'Adding load case'});

      // mutate DB
      const mutate = {
        variables: {
          data: {
            dutyCycle,
            name,
            duration
          },
        },
        mutation: gql`mutation createLoadCase ($data: LoadCaseInput!) {
            createLoadCase (data: $data) {
              loadCase {
                ${requests.model.loadCaseFullQueryStr}
              }
            }
          }`,
        optimisticResponse: {
          __typename: 'Mutation',
          createLoadCase: {
            __typename: 'CreateLoadCase',
            loadCase: {
              __typename: requests.model.TYPENAME.LOAD_CASE,
              Id: Date.now().toString(),
              name,
              duration,
              config: null,
              powerloads: [],
              powerflow: [],
            },
          },
        },
        update: (cache, { data: { createLoadCase: { loadCase } } }) => {
          // Update cache of collection to avoid a network refetch.

          const options = {
            id: apolloStore.getApolloId({__typename: requests.model.TYPENAME.DUTY_CYCLE, Id: dutyCycle}),
            fragment: requests.model.fragmentDutyCycleFull,
          };

          // Read the data from our cache for this query.
          const data = cache.readFragment(options);
          // Modify the query data object.
          data.loadCases.push(loadCase);
          // Write modified data back to the cache.
          cache.writeFragment({...options, data});
        },
      };
      const { data: { createLoadCase: { loadCase } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return loadCase;
    },
    async updateLoadCase({ commit }, payload) {
      // API mutation to update a load case.
      // Payload must be object of UpdateLoadCaseInput type per GraphQL API.
      // Apollo automatically handles the cache update.
      // No return value.

      commit('SET_STATUS', {message: 'Updating load case'});

      const mutate = {
        variables: {
          data: {...payload}
        },
        mutation: gql`mutation updateLoadCase ($data: UpdateLoadCaseInput!) {
            updateLoadCase (data: $data) {
              loadCase {
                ${requests.model.loadCaseQueryStr}
              }
            }
          }`,
      };
      await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
    },
    async removeLoadCase({ state, commit, dispatch }, {dutyCycle, Id}) {
      // API mutation to remove load case from a duty cycle.
      // `dutyCycle` is the duty cycle Id for updating cache.
      // Returns boolean to indicate if removal was successful.

      commit('SET_STATUS', {message: 'Removing load case'});

      // mutate DB
      const mutate = {
        variables: {
          Id: Id,
        },
        mutation: gql`mutation removeLoadCase($Id: ID!) {
            removeLoadCase(Id: $Id) {
              success
            }
          }`,
        optimisticResponse: {
          __typename: 'Mutation',
          removeLoadCase: {
            __typename: 'RemoveLoadCase',
            success: true,
          },
        },
        update: (cache, { data: { removeLoadCase: { success } } }) => {
          // Update cache of collection to avoid a network refetch.

          if (success) {
            const options = {
              id: apolloStore.getApolloId({__typename: requests.model.TYPENAME.DUTY_CYCLE, Id: dutyCycle}),
              fragment: requests.model.fragmentDutyCycleTree,
            };

            // Read the data from our cache for this query.
            const data = cache.readFragment(options);
            // Modify the query data object.
            removeObjectById(data.loadCases, Id);
            // Write modified data back to the cache.
            cache.writeFragment({...options, data});

            // unset load case
            if (state.analyze.loadCase.Id === Id) dispatch('clearLoadCase');
          }
        },
      };
      const { data: { removeLoadCase: { success } } } = await apolloMutate(mutate)
        .finally(() => commit('CLEAR_STATUS'));

      return success;
    },
    async removeLoadCases({ commit, dispatch }, {dutyCycle, arrId}) {
      // API mutation to remove multiple load cases from a duty cycle.
      // `dutyCycle` is the duty cycle Id for updating cache.
      // Returns boolean array to indicate if removal was successful for each load case.
      // PLACEHOLDER {batch} look into proper batching techniques with Apollo

      if (arrId.length === 0) return [];
      commit('SET_STATUS', {message: 'Removing load cases'});

      // function to synthesize query string
      function removeLoadCase(i) {
        return `
          remove${i}: removeLoadCase(Id: $Id${i}) {
            success
          }\n`;
      }

      // synthesize variables, input string, and query string
      let variables = {};
      let removeLoadCasesVariables = ``;
      let removeLoadCasesQueryStr = ``;
      arrId.forEach((Id, index) => {
        variables[`Id${index}`] = Id;
        removeLoadCasesVariables += `$Id${index}: ID!\n`;
        removeLoadCasesQueryStr += removeLoadCase(index);
      });

      // mutate DB
      const mutate = {
        variables: variables,
        mutation: gql`mutation removeLoadCases(${removeLoadCasesVariables}) {
            ${removeLoadCasesQueryStr}
          }`,
        update: (cache, { data }) => {
          // Update cache of collection to avoid a network refetch.

          const options = {
            id: apolloStore.getApolloId({__typename: requests.model.TYPENAME.DUTY_CYCLE, Id: dutyCycle}),
            fragment: requests.model.fragmentDutyCycleTree,
          };

          // Read the data from our cache for this query.
          const dataFragment = cache.readFragment(options);
          // Modify the query data object.
          Object.values(data).forEach((removeLoadCase, i) => {
            if (removeLoadCase.success) {
              removeObjectById(dataFragment.loadCases, arrId[i]);
            }
          });
          // Write modified data back to the cache.
          cache.writeFragment({...options, data: dataFragment});

          // unset load case
          dispatch('clearLoadCase');
        },
      };
      const { data } = await apolloMutate(mutate).finally(() => commit('CLEAR_STATUS'));
      const arrRemoveLoadCases = Object.values(data);

      return arrRemoveLoadCases.map(removeLoadCase => removeLoadCase.success);
    },
  },
};
